import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectUser, selectTenant } from '@/features/auth/authSlice'

/**
 * Default locale settings used when neither user nor org has configured values.
 * start_of_week uses integer (0=Monday, 6=Sunday) to match the backend.
 */
const LOCALE_DEFAULTS = {
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  start_of_week: 6, // Sunday
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
}

/**
 * Map integer start_of_week values to day names (0=Monday … 6=Sunday)
 */
const DAY_NAMES = {
  0: 'Monday',
  1: 'Tuesday',
  2: 'Wednesday',
  3: 'Thursday',
  4: 'Friday',
  5: 'Saturday',
  6: 'Sunday',
}

/**
 * Date format patterns mapped to Intl.DateTimeFormat options
 */
const DATE_FORMAT_OPTIONS = {
  'MM/DD/YYYY': { month: '2-digit', day: '2-digit', year: 'numeric' },
  'DD/MM/YYYY': { day: '2-digit', month: '2-digit', year: 'numeric' },
  'YYYY-MM-DD': { year: 'numeric', month: '2-digit', day: '2-digit' },
}

/**
 * Manual date formatting since Intl.DateTimeFormat doesn't guarantee field order
 * @param {Date} date
 * @param {string} format
 * @returns {string}
 */
function formatDateManual(date, format) {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    case 'MM/DD/YYYY':
    default:
      return `${month}/${day}/${year}`
  }
}

/**
 * Format time using locale settings
 * @param {Date} date
 * @param {string} timeFormat - '12h' or '24h'
 * @param {string} timezone
 * @returns {string}
 */
function formatTimeWithLocale(date, timeFormat, timezone) {
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: timezone,
  }

  try {
    return new Intl.DateTimeFormat('en-US', options).format(date)
  } catch {
    // Fallback for invalid timezone
    return new Intl.DateTimeFormat('en-US', {
      ...options,
      timeZone: undefined,
    }).format(date)
  }
}

/**
 * useLocale hook
 *
 * Reads effective_locale from user state (computed by backend) or falls back
 * to computing it from user overrides + org defaults + system defaults.
 *
 * Provides formatting functions for dates, times, and datetimes that respect
 * the user's locale preferences.
 *
 * @returns {{
 *   locale: { date_format: string, time_format: string, start_of_week: string, timezone: string },
 *   formatDate: (date: string|Date) => string,
 *   formatTime: (date: string|Date) => string,
 *   formatDateTime: (date: string|Date) => string,
 *   formatRelativeDate: (date: string|Date) => string,
 * }}
 */
export function useLocale() {
  const user = useSelector(selectUser)
  const tenant = useSelector(selectTenant)

  const locale = useMemo(() => {
    // If the backend computed effective_locale, use it
    if (user?.effective_locale) {
      return {
        date_format: user.effective_locale.date_format || LOCALE_DEFAULTS.date_format,
        time_format: user.effective_locale.time_format || LOCALE_DEFAULTS.time_format,
        start_of_week: user.effective_locale.start_of_week ?? LOCALE_DEFAULTS.start_of_week,
        timezone: user.effective_locale.timezone || LOCALE_DEFAULTS.timezone,
      }
    }

    // Otherwise compute locally: user overrides > tenant defaults > system defaults
    // Note: start_of_week uses ?? instead of || because 0 (Monday) is a valid value
    return {
      date_format:
        user?.preferred_date_format ||
        tenant?.date_format ||
        LOCALE_DEFAULTS.date_format,
      time_format:
        user?.preferred_time_format ||
        tenant?.time_format ||
        LOCALE_DEFAULTS.time_format,
      start_of_week:
        user?.preferred_start_of_week ??
        tenant?.start_of_week ??
        LOCALE_DEFAULTS.start_of_week,
      timezone:
        user?.preferred_timezone ||
        tenant?.default_timezone ||
        LOCALE_DEFAULTS.timezone,
    }
  }, [user, tenant])

  const formatDate = useMemo(() => {
    return (date) => {
      if (!date) return ''
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      return formatDateManual(dateObj, locale.date_format)
    }
  }, [locale.date_format])

  const formatTime = useMemo(() => {
    return (date) => {
      if (!date) return ''
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      return formatTimeWithLocale(dateObj, locale.time_format, locale.timezone)
    }
  }, [locale.time_format, locale.timezone])

  const formatDateTime = useMemo(() => {
    return (date) => {
      if (!date) return ''
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''
      return `${formatDateManual(dateObj, locale.date_format)} ${formatTimeWithLocale(dateObj, locale.time_format, locale.timezone)}`
    }
  }, [locale.date_format, locale.time_format, locale.timezone])

  const formatRelativeDate = useMemo(() => {
    return (date) => {
      if (!date) return ''
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return ''

      const now = new Date()
      const diffMs = now - dateObj
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffSecs < 60) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`

      // Fall back to formatted date
      return formatDateManual(dateObj, locale.date_format)
    }
  }, [locale.date_format])

  return {
    locale,
    formatDate,
    formatTime,
    formatDateTime,
    formatRelativeDate,
  }
}

/**
 * Exported constants for use in UI dropdowns
 */
export const DATE_FORMAT_OPTIONS_LIST = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
]

export const TIME_FORMAT_OPTIONS_LIST = [
  { value: '12h', label: '12-hour (2:30 PM)' },
  { value: '24h', label: '24-hour (14:30)' },
]

export const START_OF_WEEK_OPTIONS = [
  { value: '6', label: 'Sunday' },
  { value: '0', label: 'Monday' },
  { value: '5', label: 'Saturday' },
]

export { LOCALE_DEFAULTS, DAY_NAMES }
